import React, { createContext, useContext, useState, useEffect } from 'react'
import { auth, setAccessToken } from '../lib/api-client'

interface User {
  id: string
  email: string
  displayName: string
  role: 'admin' | 'manager' | 'user' | 'viewer' | 'escaperoute_user'
  hasEscapeRouteAccess?: boolean
  has_escaperoute_access?: boolean // Support both camelCase and snake_case
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true) // Start with true - check localStorage on mount

  // Restore user session from localStorage on mount
  useEffect(() => {
    console.log('AuthProvider mounted - checking localStorage for existing session')
    setIsLoading(true)

    // Restore user from localStorage if available
    const storedUser = localStorage.getItem('user')
    const storedToken = localStorage.getItem('accessToken') // Read directly from localStorage, not from api-client

    console.log('🔍 AuthProvider: storedUser =', storedUser ? 'found' : 'null', ', storedToken =', storedToken ? 'found' : 'null')

    // Check if stored token is a dev-bypass token (invalid in production)
    const isDevBypassToken = storedToken === 'dev-bypass-token' || storedToken === 'undefined' || storedToken === 'null'

    if (isDevBypassToken) {
      console.log('⚠️ AuthProvider: Dev bypass token found in storage - clearing')
      localStorage.removeItem('user')
      localStorage.removeItem('accessToken')
      setAccessToken(null)
    } else if (storedUser && storedToken) {
      try {
        const userData = JSON.parse(storedUser)
        if (userData && userData.id && userData.email) {
          console.log('✅ Restoring user session from localStorage:', userData.email)
          setUser(userData)
          setAccessToken(storedToken) // This will sync api-client's token variable

          // Sliding-window refresh: swap the restored (still-valid) 7-day token for a
          // fresh one so a device that opens the app weekly never reaches expiry — the
          // root cause of protected mutations silently failing on mobile. Fire-and-forget;
          // a 401 here just means the token already expired, which the normal login
          // redirect (cache-level 401 handler) already covers.
          auth
            .refreshSimple()
            .then((res) => {
              if (res?.success && res.accessToken) {
                setAccessToken(res.accessToken)
                if (res.user) {
                  localStorage.setItem('user', JSON.stringify(res.user))
                  setUser(res.user)
                }
                console.log('🔄 AuthProvider: session token refreshed (expiry slid forward)')
              }
            })
            .catch((err) => {
              // Non-fatal: keep the existing token; if it's truly expired the next
              // protected request will trigger the login redirect.
              console.log('ℹ️ AuthProvider: token refresh skipped:', err?.response?.status || err?.message)
            })
        } else {
          console.log('❌ Invalid user data in localStorage, clearing')
          localStorage.removeItem('user')
          localStorage.removeItem('accessToken')
          setAccessToken(null)
        }
      } catch (error) {
        console.error('❌ Failed to parse user data from localStorage:', error)
        // Clear corrupted data
        localStorage.removeItem('user')
        localStorage.removeItem('accessToken')
        setAccessToken(null)
      }
    } else {
      console.log('ℹ️ No stored session found - user must log in')
    }

    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    try {
      console.log('🔑 AuthContext: Attempting login for:', email)
      const response = await auth.login(email, password)
      console.log('📦 AuthContext: Login response received:', { success: response.success, hasUser: !!response.user, hasToken: !!response.accessToken })

      if (response.success && response.accessToken && response.user) {
        console.log('💾 AuthContext: Saving token and user to localStorage')
        setAccessToken(response.accessToken)
        localStorage.setItem('user', JSON.stringify(response.user))

        console.log('👤 AuthContext: Setting user state:', response.user.email)
        setUser(response.user)

        console.log('✅ AuthContext: Login complete, isAuthenticated should now be true')
      } else {
        throw new Error(response.error || 'Login failed')
      }
    } catch (error: any) {
      console.error('❌ AuthContext: Login failed:', error)
      throw new Error(error.response?.data?.error || error.message || 'Login failed')
    }
  }

  const logout = async () => {
    try {
      console.log('Logging out...')
      await auth.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setUser(null)
      setAccessToken(null)
      localStorage.removeItem('user')
      console.log('Logged out successfully')
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      isLoading,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
